import { createSlice } from "@reduxjs/toolkit";

import {
  getRobots,
  getOperationHours,
  getPerformanceWeldHours,
  getPerformanceWeldInches,
  getProgramData,
  getWeldRuns,
  getTier,
  getUsage,
  updateUsage,
  getRobotPerformanceWeldHours,
  getRobotPerformanceWeldInches,
  getRobotPerformanceOperatorsWeldHours,
  getRobotPerformanceOperatorsWeldInches,
  getWeldTestReports,
  createWeldTestReport,
  getWeldTestReport,
  updateWeldTestReport,
  deleteWeldTestReport,
} from "../../services/api/robot";

import { getFromS3URL } from "../../services/api";

import { getCustomersFromHubspot } from "../../services/api/hubspot";

import {
  latestSummary,
  softwareVersion,
  diskSpace,
  currentFiles,
} from "../../libs/utils";

import { robots_with_coordinates } from "../../libs/mockData";

const robotSlice = createSlice({
  name: "robot",
  initialState: {
    fetching: false,
    fetchingError: null,
  },

  reducers: {
    fetchListRequest: (state) => ({
      ...state,
      fetching: true,
      fetchingError: null,
    }),
    fetchListFailure: (state, action) => ({
      ...state,
      fetching: false,
      fetchingError: action.error,
    }),
    fetchListSuccess: (state, action) => ({
      ...state,
      fetching: false,
      fetchingError: null,
      robots: action.payload.robots,
    }),

    fetchRequest: (state) => ({
      ...state,
      fetching: true,
      fetchingError: null,
    }),
    fetchFailure: (state, action) => ({
      ...state,
      fetching: false,
      fetchingError: action.error,
    }),
    fetchSuccess: (state, action) => ({
      ...state,
      fetching: false,
      fetchingError: null,
    }),

    fetchOperationHoursRequest: (state) => ({
      ...state,
      fetching: true,
      fetchingError: null,
    }),
    fetchOperationHoursFailure: (state, action) => ({
      ...state,
      fetching: false,
      fetchingError: action.error,
    }),
    fetchOperationHoursSuccess: (state, action) => ({
      ...state,
      fetching: false,
      fetchingError: null,
      operation_hours: action.payload.data,
    }),

    fetchPerformanceWeldHoursRequest: (state) => ({
      ...state,
      fetching: true,
      fetchingError: null,
    }),
    fetchPerformanceWeldHoursFailure: (state, action) => ({
      ...state,
      fetching: false,
      fetchingError: action.error,
    }),
    fetchPerformanceWeldHoursSuccess: (state, action) => ({
      ...state,
      fetching: false,
      fetchingError: null,
      weld_hours: action.payload.data,
    }),

    fetchPerformanceWeldInchesRequest: (state) => ({
      ...state,
      fetching: true,
      fetchingError: null,
    }),
    fetchPerformanceWeldInchesFailure: (state, action) => ({
      ...state,
      fetching: false,
      fetchingError: action.error,
    }),
    fetchPerformanceWeldInchesSuccess: (state, action) => ({
      ...state,
      fetching: false,
      fetchingError: null,
      weld_inches: action.payload.data,
    }),

    fetchWeldRunsRequest: (state) => ({
      ...state,
      fetching: true,
      fetchingError: null,
    }),
    fetchWeldRunsFailure: (state, action) => ({
      ...state,
      fetching: false,
      fetchingError: action.error,
    }),
    fetchWeldRunsSuccess: (state, action) => ({
      ...state,
      fetching: false,
      fetchingError: null,
      weldRun: {
        ...state.weldRun,
        ...action.payload.weldRun,
      },
    }),

    fetchTierRequest: (state) => ({
      ...state,
      fetching: true,
      fetchingError: null,
    }),
    fetchTierFailure: (state, action) => ({
      ...state,
      fetching: false,
      fetchingError: action.error,
    }),
    fetchTierSuccess: (state, action) => ({
      ...state,
      fetching: false,
      tier: {
        ...state.tier,
        ...action.payload.tier,
      },
    }),

    updateUsageRequest: (state) => ({
      ...state,
      fetching: true,
      fetchingError: null,
    }),
    updateUsageFailure: (state, action) => ({
      ...state,
      fetching: false,
      fetchingError: action.error,
    }),
    updateUsageSuccess: (state, action) => ({
      ...state,
      fetching: false,
      tier: {
        ...state.tier,
        ...action.payload.tier,
      },
    }),

    fetchUsageListRequest: (state) => ({
      ...state,
      fetching: true,
      fetchingError: null,
    }),
    fetchUsageListFailure: (state, action) => ({
      ...state,
      fetching: false,
      fetchingError: action.error,
    }),
    fetchUsageListSuccess: (state, action) => ({
      ...state,
      fetching: false,
      fetchingError: null,
      usage: {
        ...state.tier,
        ...action.payload.usage,
      },
    }),

    fetchWeldHoursListRequest: (state) => ({
      ...state,
      fetching: true,
      fetchingError: null,
    }),
    fetchWeldHoursListSuccess: (state, action) => ({
      ...state,
      weldHours: {
        ...state.weldHours,
        ...action.payload.weldHours,
      },
    }),
    fetchWeldHoursListFailure: (state, action) => ({
      ...state,
      fetching: false,
      fetchingError: action.error,
    }),

    fetchWeldInchesListRequest: (state) => ({
      ...state,
      fetching: true,
      fetchingError: null,
    }),
    fetchWeldInchesListSuccess: (state, action) => ({
      ...state,
      weldInches: {
        ...state.weldInches,
        ...action.payload.weldInches,
      },
    }),
    fetchWeldInchesListFailure: (state, action) => ({
      ...state,
      fetching: false,
      fetchingError: action.error,
    }),

    fetchOperatorsWeldHoursListRequest: (state) => ({
      ...state,
      fetching: true,
      fetchingError: null,
    }),
    fetchOperatorsWeldHoursListSuccess: (state, action) => ({
      ...state,
      weldHoursOperators: {
        ...state.weldHoursOperators,
        ...action.payload.weldHoursOperators,
      },
    }),
    fetchOperatorsWeldHoursListFailure: (state, action) => ({
      ...state,
      fetching: false,
      fetchingError: action.error,
    }),

    fetchOperatorsWeldInchesListRequest: (state) => ({
      ...state,
      fetching: true,
      fetchingError: null,
    }),
    fetchOperatorsWeldInchesListSuccess: (state, action) => ({
      ...state,
      weldInchesOperators: {
        ...state.weldInchesOperators,
        ...action.payload.weldInchesOperators,
      },
    }),
    fetchOperatorsWeldInchesListFailure: (state, action) => ({
      ...state,
      fetching: false,
      fetchingError: action.error,
    }),

    fetchPerformanceStatsRequest: (state) => ({
      ...state,
      fetching: true,
      fetchingError: null,
    }),
    fetchPerformanceStatsSuccess: (state, action) => ({
      ...state,
      stats: {
        ...state.stats,
        ...action.payload.stats,
      },
    }),
    fetchPerformanceStatsFailure: (state, action) => ({
      ...state,
      fetching: false,
      fetchingError: action.error,
    }),

    fetchRobotWeldTestReportsListRequest: (state, action) => ({
      ...state,
      fetching: true,
      fetchingError: null,
    }),
    fetchRobotWeldTestReportsListSuccess: (state, action) => ({
      ...state,
      fetching: false,
      weldTestReports: action.payload.weldTestReports,
    }),
    fetchRobotWeldTestReportsListFailure: (state, action) => ({
      ...state,
      fetching: false,
      fetchingError: action.error,
    }),

    createRobotWeldTestReportRequest: (state, action) => ({
      ...state,
      fetching: true,
      fetchingError: null,
    }),
    createRobotWeldTestReportSuccess: (state, action) => ({
      ...state,
      fetching: false,
      weldTestReports: [...state.weldTestReports, action.payload.data],
    }),
    createRobotWeldTestReportFailure: (state, action) => ({
      ...state,
      fetching: false,
      fetchingError: action.error,
    }),

    fetchRobotWeldTestReportRequest: (state, action) => ({
      ...state,
      fetching: true,
      fetchingError: null,
    }),
    fetchRobotWeldTestReportSuccess: (state, action) => ({
      ...state,
      fetching: false,
    }),
    fetchRobotWeldTestReportFailure: (state, action) => ({
      ...state,
      fetching: false,
      fetchingError: action.error,
    }),

    updateRobotWeldTestReportRequest: (state, action) => ({
      ...state,
      fetching: true,
      fetchingError: null,
    }),
    updateRobotWeldTestReportSuccess: (state, action) => ({
      ...state,
      fetching: false,
      weldTestReports: [...state.weldTestReports].map((obj) => ({
        ...obj,
        ...[action.payload.data].find((o) => o.id === obj.id),
      })),
    }),
    updateRobotWeldTestReportFailure: (state, action) => ({
      ...state,
      fetching: false,
      fetchingError: action.error,
    }),

    removeRobotWeldTestReportRequest: (state, action) => ({
      ...state,
      fetching: true,
      fetchingError: null,
    }),
    removeRobotWeldTestReportSuccess: (state, action) => ({
      ...state,
      fetching: false,
      weldTestReports: [...state.weldTestReports].map((obj) => ({
        ...obj,
        ...[action.payload.data].find((o) => o.id === obj.id),
      })),
    }),
    removeRobotWeldTestReportFailure: (state, action) => ({
      ...state,
      fetching: false,
      fetchingError: action.error,
    }),
  },
});

export const {
  fetchListRequest,
  fetchListFailure,
  fetchListSuccess,
  fetchRequest,
  fetchFailure,
  fetchSuccess,
  fetchOperationHoursRequest,
  fetchOperationHoursFailure,
  fetchOperationHoursSuccess,
  fetchPerformanceWeldHoursRequest,
  fetchPerformanceWeldHoursFailure,
  fetchPerformanceWeldHoursSuccess,
  fetchPerformanceWeldInchesRequest,
  fetchPerformanceWeldInchesFailure,
  fetchPerformanceWeldInchesSuccess,
  fetchWeldRunsRequest,
  fetchWeldRunsFailure,
  fetchWeldRunsSuccess,
  fetchHubSpotInfoRequest,
  fetchHubSpotInfoSuccess,
  fetchHubSpotInfoFailure,
  fetchTierRequest,
  fetchTierFailure,
  fetchTierSuccess,
  updateUsageRequest,
  updateUsageFailure,
  updateUsageSuccess,
  fetchUsageListRequest,
  fetchUsageListFailure,
  fetchUsageListSuccess,

  // ##############################
  // Performance
  // ##############################
  fetchWeldHoursListRequest,
  fetchWeldHoursListSuccess,
  fetchWeldHoursListFailure,

  fetchWeldInchesListRequest,
  fetchWeldInchesListSuccess,
  fetchWeldInchesListFailure,

  fetchOperatorsWeldHoursListRequest,
  fetchOperatorsWeldHoursListSuccess,
  fetchOperatorsWeldHoursListFailure,

  fetchOperatorsWeldInchesListRequest,
  fetchOperatorsWeldInchesListSuccess,
  fetchOperatorsWeldInchesListFailure,

  fetchPerformanceStatsRequest,
  fetchPerformanceStatsSuccess,
  fetchPerformanceStatsFailure,

  // ##############################
  // Test Reports
  // ##############################
  fetchRobotWeldTestReportsListRequest,
  fetchRobotWeldTestReportsListSuccess,
  fetchRobotWeldTestReportsListFailure,

  createRobotWeldTestReportRequest,
  createRobotWeldTestReportSuccess,
  createRobotWeldTestReportFailure,

  fetchRobotWeldTestReportRequest,
  fetchRobotWeldTestReportSuccess,
  fetchRobotWeldTestReportFailure,

  updateRobotWeldTestReportRequest,
  updateRobotWeldTestReportSuccess,
  updateRobotWeldTestReportFailure,

  removeRobotWeldTestReportRequest,
  removeRobotWeldTestReportSuccess,
  removeRobotWeldTestReportFailure,
} = robotSlice.actions;

export default robotSlice.reducer;

// export const {
//   selectById: selectRobotById,
//   selectIds: selectRobotIds,
//   selectEntities: selectRobotEntities,
//   selectAll: selectAllRobots,
//   selectTotal: selectTotalRobots,
// } = robotsAdapter.getSelectors((state) => state.robots);

export const getRobotsList = () => async (dispatch) => {
  try {
    dispatch(fetchListRequest());

    // const data = await getRobots();
    const data = [
      {
        id: 2254,
        name: "SWR-2254",
        customer: "ACRFuller",
        location: "Lively, ON",
        groups: [{ id: 64, name: "SWR-2254" }],
      },
      {
        id: 2250,
        name: "SWR-2250",
        customer: "ArdenEngineeringConstructors",
        location: "Pawtucket, RI",
        groups: [{ id: 54, name: "SWR-2250" }],
      },
      {
        id: 2249,
        name: "SWR-2249",
        customer: "TBD",
        location: "TBD",
        groups: [{ id: 53, name: "SWR-2249" }],
      },
      {
        id: 2248,
        name: "SWR-2248",
        customer: "TBD",
        location: "TBD",
        groups: [{ id: 52, name: "SWR-2248" }],
      },
      {
        id: 2151,
        name: "SWR-2151",
        customer: "AbicorBinzel",
        location: "Germany-CE",
        groups: [{ id: 51, name: "SWR-2151" }],
      },
      {
        id: 2147,
        name: "SWR-2147",
        customer: "MMC",
        location: "Eden Prairie, MN",
        groups: [{ id: 50, name: "SWR-2147" }],
      },
      {
        id: 2146,
        name: "SWR-2146",
        customer: "Cox Engineering",
        location: "Randolph, MA",
        groups: [{ id: 47, name: "SWR-2146" }],
      },
      {
        id: 2145,
        name: "SWR-2145",
        customer: "TBD",
        location: "TBD",
        groups: [{ id: 46, name: "SWR-2145" }],
      },
      {
        id: 2144,
        name: "SWR-2144",
        customer: "Abicor",
        location: "China",
        groups: [{ id: 48, name: "SWR-2144" }],
      },
      {
        id: 2143,
        name: "SWR-2143",
        customer: "Sage Creek",
        location: "Penticton, BC",
        groups: [{ id: 44, name: "SWR-2143" }],
      },
      {
        id: 2142,
        name: "SWR-2142",
        customer: "ACCO",
        location: "Los Angeles, CA",
        groups: [{ id: 40, name: "SWR-2142" }],
      },
      {
        id: 2141,
        name: "SWR-2141",
        customer: "Worley",
        location: "Blackfalds, AB",
        groups: [
          { id: 39, name: "SWR-2141" },
          { id: 45, name: "Worley" },
        ],
      },
      {
        id: 2140,
        name: "SWR-2140",
        customer: "Worley",
        location: "Blackfalds, AB",
        groups: [
          { id: 45, name: "Worley" },
          { id: 38, name: "SWR-2140" },
        ],
      },
      {
        id: 2139,
        name: "SWR-2139",
        customer: "ACCO",
        location: "San Leandro, CA",
        groups: [{ id: 37, name: "SWR-2139" }],
      },
      {
        id: 2138,
        name: "SWR-2138",
        customer: "GroupePlombaction",
        location: "Victoriaville, QC",
        groups: [{ id: 36, name: "SWR-2138" }],
      },
      {
        id: 2137,
        name: "SWR-2137",
        customer: "HarderMechanical",
        location: "Portland, OR",
        groups: [{ id: 35, name: "SWR-2137" }],
      },
      {
        id: 2136,
        name: "SWR-2136",
        customer: "CohnandGregory",
        location: "Forth Worth, TX",
        groups: [{ id: 34, name: "SWR-2136" }],
      },
      {
        id: 2135,
        name: "SWR-2135",
        customer: "SiliconValleyMechanical",
        location: "San Jose, CA",
        groups: [{ id: 33, name: "SWR-2135" }],
      },
      {
        id: 2134,
        name: "SWR-2134",
        customer: "OnSite3d",
        location: "Clairmont, AB",
        groups: [{ id: 42, name: "SWR-2134" }],
      },
      {
        id: 2133,
        name: "SWR-2133",
        customer: "RozenSteelworks",
        location: "Spruce Grove, AB",
        groups: [{ id: 32, name: "SWR-2133" }],
      },
      {
        id: 2132,
        name: "SWR-2132",
        customer: "AbicorBinzel",
        location: "TBD",
        groups: [{ id: 31, name: "SWR-2132" }],
      },
      {
        id: 2031,
        name: "SWR-2031",
        customer: "BlackandMacdonald",
        location: "Ottawa, ON",
        groups: [
          { id: 59, name: "Black & McDonald" },
          { id: 30, name: "SWR-2031" },
        ],
      },
      {
        id: 2030,
        name: "SWR-2030",
        customer: "WesternAllied",
        location: "Menlo Park, CA",
        groups: [{ id: 29, name: "SWR-2030" }],
      },
      {
        id: 2029,
        name: "SWR-2029",
        customer: "VictoriaAirConditioning",
        location: "Victoria, TX",
        groups: [{ id: 28, name: "SWR-2029" }],
      },
      {
        id: 2028,
        name: "SWR-2028",
        customer: "Riggs-Distler",
        location: "Cherry Hill, NJ",
        groups: [{ id: 27, name: "SWR-2028" }],
      },
      {
        id: 2027,
        name: "SWR-2027",
        customer: "McAbee",
        location: "Alabama",
        groups: [{ id: 26, name: "SWR-2027" }],
      },
      {
        id: 2026,
        name: "SWR-2026",
        customer: "KihewEagle",
        location: "Melville, SK",
        groups: [{ id: 25, name: "SWR-2026" }],
      },
      {
        id: 2025,
        name: "SWR-2025",
        customer: "Kiewit",
        location: "Edmonton, AB",
        groups: [{ id: 24, name: "SWR-2025" }],
      },
      {
        id: 2024,
        name: "SWR-2024",
        customer: "All-ProServices",
        location: "Port Coquitlam, BC",
        groups: [{ id: 23, name: "SWR-2024" }],
      },
      {
        id: 2023,
        name: "SWR-2023",
        customer: "OnSite3d",
        location: "Red Deer, AB",
        groups: [{ id: 22, name: "SWR-2023" }],
      },
      {
        id: 2022,
        name: "SWR-2022",
        customer: "NTact",
        location: "Texas",
        groups: [{ id: 21, name: "SWR-2022" }],
      },
      {
        id: 2021,
        name: "SWR-2021",
        customer: "RJMech",
        location: "AL",
        groups: [{ id: 20, name: "SWR-2021" }],
      },
      {
        id: 2020,
        name: "SWR-2020",
        customer: "FWSims",
        location: "NewYork",
        groups: [{ id: 19, name: "SWR-2020" }],
      },
      {
        id: 1919,
        name: "SWR-1919",
        customer: "AbicorBinzel",
        location: "China",
        groups: [{ id: 18, name: "SWR-1919" }],
      },
      {
        id: 1918,
        name: "SWR-1918",
        customer: "Primoris",
        location: "Edmonton, AB",
        groups: [{ id: 17, name: "SWR-1918" }],
      },
      {
        id: 1917,
        name: "SWR-1917",
        customer: "Ganotec",
        location: "Quebec",
        groups: [{ id: 16, name: "SWR-1917" }],
      },
      {
        id: 1916,
        name: "SWR-1916",
        customer: "Worley",
        location: "Houston",
        groups: [{ id: 15, name: "SWR-1916" }],
      },
      {
        id: 1915,
        name: "SWR-1915",
        customer: "Worley",
        location: "Edmonton, AB",
        groups: [{ id: 14, name: "SWR-1915" }],
      },
      {
        id: 1914,
        name: "SWR-1914",
        customer: "Worley",
        location: "Blackfalds, AB",
        groups: [
          { id: 45, name: "Worley" },
          { id: 13, name: "SWR-1914" },
        ],
      },
      {
        id: 1913,
        name: "SWR-1913",
        customer: "PMP",
        location: "Maple Ridge, BC",
        groups: [{ id: 12, name: "SWR-1913" }],
      },
      {
        id: 1912,
        name: "SWR-1912",
        customer: "SeaSpan",
        location: "North Vancouver, BC",
        groups: [{ id: 11, name: "SWR-1912" }],
      },
      {
        id: 1911,
        name: "SWR-1911",
        customer: "AbicorBinzel",
        location: "Germany",
        groups: [{ id: 10, name: "SWR-1911" }],
      },
      {
        id: 1910,
        name: "SWR-1910",
        customer: "Novarc",
        location: "North Vancouver, BC",
        groups: [{ id: 9, name: "SWR-1910" }],
      },
      {
        id: 1909,
        name: "SWR-1909",
        customer: "AbicorBinzel",
        location: "Dubai",
        groups: [{ id: 8, name: "SWR-1909" }],
      },
      {
        id: 1805,
        name: "SWR-1805",
        customer: "Kiewit",
        location: "Texas",
        groups: [{ id: 4, name: "SWR-1805" }],
      },
      {
        id: 188,
        name: "SWR-188",
        customer: "McKenney",
        location: "Atlanta, GA",
        groups: [{ id: 7, name: "SWR-188" }],
      },
      {
        id: 187,
        name: "SWR-187",
        customer: "WWGay",
        location: "Jacksonville, Fl",
        groups: [{ id: 6, name: "SWR-187" }],
      },
      {
        id: 186,
        name: "SWR-186",
        customer: "Kiewit",
        location: "Texas",
        groups: [{ id: 5, name: "SWR-186" }],
      },
      {
        id: 183,
        name: "SWR-183",
        customer: "AbicorBinzel",
        location: "Houston",
        groups: [{ id: 3, name: "SWR-183" }],
      },
      {
        id: 1,
        name: "SWR-171",
        customer: "Novarc",
        location: "North Vancouver, BC",
        groups: [{ id: 2, name: "SWR-171" }],
      },
    ];

    const customers = await getRobotInfo()();

    const robots = await Promise.all(
      data.map(async (item) => {
        const info = await getRobotData(item.id)();
        /*
          FIXME: This uses manual dummy data
          Coordinate information will be retrieved from Hubspot
        */
        const { coordinates, country, swr_information } =
          robots_with_coordinates.find((i) => {
            return i.id === item.id;
          }) || {};

        const hubspot_information =
          (customers &&
            customers.length > 0 &&
            customers.find((i) => {
              return (
                i.properties.unit_number ===
                `SWR_${item.id === 1 ? 171 : item.id}`
              );
            })) ||
          {};

        return {
          ...item,
          ...info,
          coordinates,
          country,
          swr_information,
          hubspot_information: { },
        };
      })
    );

    dispatch(fetchListSuccess({ robots }));
  } catch (err) {
    dispatch(fetchListFailure(err.toString()));
  }
};

export const getRobotsTopPerformanceWeldHours = () => async (dispatch) => {
  try {
    dispatch(fetchOperationHoursRequest());

    const data = await getPerformanceWeldHours();

    dispatch(fetchOperationHoursSuccess({ data }));
  } catch (err) {
    dispatch(fetchOperationHoursFailure(err.toString()));
  }
};

export const getRobotsTopPerformanceWeldInches = () => async (dispatch) => {
  try {
    dispatch(fetchOperationHoursRequest());

    const data = await getPerformanceWeldInches();

    dispatch(fetchOperationHoursSuccess({ data }));
  } catch (err) {
    dispatch(fetchOperationHoursFailure(err.toString()));
  }
};

export const getRobotsOperationHours = () => async (dispatch) => {
  try {
    dispatch(fetchOperationHoursRequest());

    const data = await getOperationHours();

    dispatch(fetchOperationHoursSuccess({ data }));
  } catch (err) {
    dispatch(fetchOperationHoursFailure(err.toString()));
  }
};

// Get single robot info via API
export const getRobotData = (id) => async (dispatch) => {
  try {
    const { data } = await getProgramData(id === "171" ? 1 : id, "health_logs");
    // software_version.json
    // nh.log
    // disk_space.json
    // latest Data-YYYYMMDD.json
    const summaryItem = latestSummary(data);
    const softwareVersionItem = softwareVersion(data);
    const diskSpaceItem = diskSpace(data);
    const currentFilesItem = currentFiles(data);

    const summary = await getFromS3URL(summaryItem.url);
    const versions = await getFromS3URL(softwareVersionItem.url);
    const space = await getFromS3URL(diskSpaceItem.url);

    // This is new feature added along with NovSync > 2.0.3
    const current_files = currentFilesItem
      ? await getFromS3URL(currentFilesItem.url)
      : {};

    return { summary, versions, space, current_files };
  } catch (err) {}
};

// getting it from Hubspot API
export const getRobotInfo = () => async (dispatch) => {
  try {
    const data = await getCustomersFromHubspot();

    return data;
  } catch (err) {}
};

export const getRobotWeld = (id, query) => async (dispatch) => {
  try {
    dispatch(fetchWeldRunsRequest());

    const { data } = await getWeldRuns(id === "171" ? 1 : id, query);

    dispatch(fetchWeldRunsSuccess({ weldRun: { [id]: data } }));
  } catch (err) {
    dispatch(fetchWeldRunsFailure(err.toString()));
  }
};

export const getRobotTier = (id) => async (dispatch) => {
  try {
    dispatch(fetchTierRequest());

    const { data } = await getTier(id === "171" ? 1 : id);

    dispatch(fetchTierSuccess({ tier: { [id]: data } }));
  } catch (err) {
    dispatch(fetchTierFailure(err.toString()));
  }
};

export const updateRobotUsage = (id, payload) => async (dispatch) => {
  try {
    dispatch(updateUsageRequest());

    const { data } = await updateUsage(id === "171" ? 1 : id, {
      used: payload.size,
      note: payload.fileName,
      activity: payload.activity,
    });

    dispatch(updateUsageSuccess({ tier: { [id]: data } }));
  } catch (err) {
    dispatch(updateUsageFailure(err.toString()));
  }
};

export const fetchRobotUsageList = (id, payload) => async (dispatch) => {
  try {
    dispatch(fetchUsageListRequest());

    const { data } = await getUsage(id === "171" ? 1 : id, {
      month: payload.month,
      year: payload.year,
    });

    dispatch(fetchUsageListSuccess({ usage: { [id]: data } }));
  } catch (err) {
    dispatch(fetchUsageListFailure(err.toString()));
  }
};

export const getRobotStats =
  (id, { initial, recent }) =>
  async (dispatch) => {
    try {
      dispatch(fetchPerformanceStatsRequest());

      const totalWeldTimes = await getRobotPerformanceWeldHours(
        id === "171" ? 1 : id,
        { start: initial }
      );
      const totalWeldInches = await getRobotPerformanceWeldInches(
        id === "171" ? 1 : id,
        { start: initial }
      );
      const weldTimes = await getRobotPerformanceWeldHours(
        id === "171" ? 1 : id,
        { start: recent }
      );
      const weldInches = await getRobotPerformanceWeldInches(
        id === "171" ? 1 : id,
        { start: recent }
      );

      dispatch(
        fetchPerformanceStatsSuccess({
          stats: {
            [id]: {
              totalWeldTimes,
              totalWeldInches,
              weldTimes,
              weldInches,
            },
          },
        })
      );
    } catch (err) {
      dispatch(fetchPerformanceStatsFailure(err.toString()));
    }
  };

export const getRobotWeldHoursList = (id, query) => async (dispatch) => {
  try {
    dispatch(fetchWeldHoursListRequest());

    const data = await getRobotPerformanceWeldHours(
      id === "171" ? 1 : id,
      query
    );

    dispatch(fetchWeldHoursListSuccess({ weldHours: { [id]: data } }));
  } catch (err) {
    dispatch(fetchWeldHoursListFailure(err.toString()));
  }
};

export const getRobotWeldInchesList = (id, query) => async (dispatch) => {
  try {
    dispatch(fetchWeldInchesListRequest());

    const data = await getRobotPerformanceWeldInches(
      id === "171" ? 1 : id,
      query
    );

    dispatch(fetchWeldInchesListSuccess({ weldInches: { [id]: data } }));
  } catch (err) {
    dispatch(fetchWeldInchesListFailure(err.toString()));
  }
};

export const getRobotOperatorsWeldHoursList =
  (id, query) => async (dispatch) => {
    try {
      dispatch(fetchOperatorsWeldHoursListRequest());

      const data = await getRobotPerformanceOperatorsWeldHours(
        id === "171" ? 1 : id,
        query
      );

      dispatch(
        fetchOperatorsWeldHoursListSuccess({
          weldHoursOperators: { [id]: data },
        })
      );
    } catch (err) {
      dispatch(fetchOperatorsWeldHoursListFailure(err.toString()));
    }
  };

export const getRobotOperatorsWeldInchesList =
  (id, query) => async (dispatch) => {
    try {
      dispatch(fetchOperatorsWeldInchesListRequest());

      const data = await getRobotPerformanceOperatorsWeldInches(
        id === "171" ? 1 : id,
        query
      );

      dispatch(
        fetchOperatorsWeldInchesListSuccess({
          weldInchesOperators: { [id]: data },
        })
      );
    } catch (err) {
      dispatch(fetchOperatorsWeldInchesListFailure(err.toString()));
    }
  };

export const getRobotWeldTestReportsList = (id, query) => async (dispatch) => {
  try {
    dispatch(fetchRobotWeldTestReportsListRequest());

    const { data } = await getWeldTestReports(id, query);

    dispatch(fetchRobotWeldTestReportsListSuccess({ weldTestReports: data }));
  } catch (err) {
    dispatch(fetchRobotWeldTestReportsListFailure(err.toString()));
  }
};
export const createRobotWeldTestReport = (id, payload) => async (dispatch) => {
  try {
    dispatch(createRobotWeldTestReportRequest());

    const { data } = await createWeldTestReport(id, payload);

    dispatch(createRobotWeldTestReportSuccess({ data }));
  } catch (err) {
    dispatch(createRobotWeldTestReportFailure(err.toString()));
  }
};

export const getRobotWeldTestReport = (params, query) => async (dispatch) => {
  try {
    dispatch(fetchRobotWeldTestReportRequest());

    const { data } = await getWeldTestReport(params, query);

    dispatch(fetchRobotWeldTestReportSuccess({ weldTestReports: data }));
  } catch (err) {
    dispatch(fetchRobotWeldTestReportFailure(err.toString()));
  }
};

export const updateRobotWeldTestReport =
  (params, payload) => async (dispatch) => {
    try {
      dispatch(updateRobotWeldTestReportRequest());

      const { data } = await updateWeldTestReport(params, payload);

      dispatch(updateRobotWeldTestReportSuccess({ data }));
    } catch (err) {
      dispatch(updateRobotWeldTestReportFailure(err.toString()));
    }
  };

export const removeRobotWeldTestReport = (params) => async (dispatch) => {
  try {
    dispatch(removeRobotWeldTestReportRequest());

    const { data } = await deleteWeldTestReport(params);

    dispatch(removeRobotWeldTestReportSuccess({ data }));
  } catch (err) {
    dispatch(removeRobotWeldTestReportFailure(err.toString()));
  }
};
